import { connect } from 'pwa-helpers';
import { BaseComponent } from '../../base-component/base-component';
import { ProductCardData } from '../../product-card/product-card-display';
import { WishList } from '../interfaces/wish-list-items.interface';
import { booleanConverter } from '../../../common/converters';
import { css, html, nothing, render, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getProductFromWishList, getRemoveProductFromWishListRequest } from '../utils/wish-list-product.util';
import store, { waitForUser } from '../../../store/store';
import { removeProductFromWishList } from '../thunks/wish-list-remove-product.thunk';
import { WishListProductAction } from '../interfaces/wish-list-update-product.interface';
import { isRemoveProductFromWishListLoading, isRemoveProductFromWishListSuccess } from '../utils/wish-list.util';
import { renderErrorToast, renderUpdateProductSuccessToast } from '../utils/wish-list-toast.util';
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';
import { fetchWishListByWishListId } from '../thunks/wish-list-fetch-wish-list-by-id.thunks';
import { fetchAllWishLists } from '../thunks/wish-list-fetch-all-wish-lists.thunks';
import { setCurrentWishList } from '../store/wish-list.store';
import '../../product-card/product-card-display/product-card-image.display';
import '../../product-card/variations/k12-product-list.display';
import './wish-list-item-comment';
import { StoreState } from '../../../store/store.interface';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';
import { selectUser } from '../../user-profile/user-profile.selectors';
import { PageTypes } from '../../../common/utils/page-util';

@customElement('mhecomm-wish-list-item-card-display')
export class WishListItemCardDisplay extends connect(store)(BaseComponent) {
  static styles = css`
    .item {
      display: flex;
      align-items: center;
      margin: 1.25rem 0;
    }

    .item img {
      width: 8.313rem;
      margin-right: 1.25rem;
    }

    @media (min-width: 768px) {
      .product-wishlist-icon {
        left: auto;
        right: 0;
      }
    }
  `;

  /**
   * The product data related to the item.
   */
  @property({ type: Object })
  productCardData?: ProductCardData;

  /**
   * The wish list related to the item.
   */
  @property({ type: Object })
  wishList?: WishList;

  /**
   * A flag to determine if the off-canvas view is enabled.
   */
  @property({ converter: booleanConverter })
  offCanvasView = false;

  /**
   * A flag to determine if add  wishlist button
   * and the comment section will be rendered
   */
  @property({ type: Boolean })
  isReadOnly = false;

  /*
   * The current logged in user profile
   */
  @state()
  private user: UserProfile | undefined;

  /**
   * The analytics instance for the wish list.
   */
  private readonly wishListAnalytics: WishListAnalytics;

  constructor() {
    super();
    this.wishListAnalytics = new WishListAnalytics();
  }

  stateChanged(state: StoreState): void {
    super.stateChanged(state);
    this.user = selectUser(state);
  }

  renderComp() {
    return this.offCanvasView ? this.getOffCanvasView() : this.getWishListView();
  }

  /**
   * Retrieves the off-canvas view.
   */
  private getOffCanvasView(): TemplateResult {
    return html`
      <div class="card rounded pb-2">
        ${!this.isReadOnly
          ? html` <div class="d-flex p-2 justify-content-start">
              ${this.getWishListButton()}
            </div>`
          : nothing}
        <div class="position-relative">
          <mhecomm-k12-product-card-display
            .centerCardImage=${false}
            .product=${this.productCardData}
            ?isWishListProduct=${true}
            .wishList=${this.wishList}
            addToCartPageType=${PageTypes.CatalogLandingPage}
            .showBusinessLabelType=${false}
            .showProgramFamily=${true}
            .showShadow=${false}
            cardBootstrapPadding="px-2"
            imageContainerStyle="product-card-image-container-small"
            imageStyle="product-card-img-small"
            productWishListPadding="pb-3 px-2"
            wishListButtonPosition="left"
          >
          </mhecomm-k12-product-card-display>
          ${!this.isReadOnly
            ? html` <div class="col-12 px-2">
                ${this.getCommentDisplay(true)}
              </div>`
            : nothing}
        </div>
        ${this.getWishListToast()}
      </div>
    `;
  }

  /**
   * Retrieves the view from the wish list item page.
   */
  private getWishListView(): TemplateResult {
    return html` <div class="card mt-4 mx-4">
      <div class="card-body position-relative px-0">
        <div class="container-fluid">
          <div class="item row mb-0">
            <div class="col-12 col-lg-3 col-md-4 col-sm-12 px-0 text-lg-center">
              ${this.getImageDisplay()}
            </div>
            <div class="col-12 col-lg-9 col-md-8 col-sm-12 px-0 mt-0">
              <mhecomm-k12-product-list-display
                .product=${this.productCardData}
                ?isWishListProduct=${true}
                .wishList=${this.wishList}
                addToCartPageType=${PageTypes.MyAccountMyListPage}
                .showBusinessLabelType=${false}
                ?isWishListItemCardView=${true}
              >
              </mhecomm-k12-product-list-display>
            </div>
          </div>
        </div>
        ${!this.isReadOnly
          ? html` <div class="container-fluid px-2 px-md-0">
                <div class="row g-0">
                  <div class="col-12 px-0 px-md-3">
                    ${this.getCommentDisplay()}
                  </div>
                </div>
              </div>
              <div class="product-wishlist-icon position-absolute top-0 px-1 px-md-3 py-2">
                ${this.getWishListButton()}
              </div>`
          : nothing}
      </div>
      ${this.getWishListToast()}
    </div>`;
  }

  /**
   * Retrieves the image display.
   */
  private getImageDisplay(): TemplateResult {
    return html` <img
      aria-label="Product Card Image"
      class="product-card-img"
      src="${this.productCardData?.image!}"
      alt="Product Title"
    />`;
  }

  /**
   * Retrieves the comment display.
   */
  private getCommentDisplay(isOffCanvasView = false): TemplateResult | typeof nothing {
    return html`
      <div class="${isOffCanvasView ? 'm-0' : 'mt-3'} mb-2">
        <mhecomm-wish-list-item-comment
          .product="${this.productCardData}"
          .wishListId=${this.wishList!.wishlist_id}
          inputComment=${this.productCardData?.comments!}
        >
        </mhecomm-wish-list-item-comment>
      </div>
    `;
  }

  /**
   * Retrieves the wish list button
   */
  private getWishListButton() {
    return html`
      <button
        id="wish-list-remove-product-${this.getProductIsbn10()}-button"
        data-automation-id="remove-product-${this.getProductIsbn10()}-wish-list"
        aria-label="Remove this product from wish list"
        class="btn btn-link text-reset border-0 p-0 wish-list-item-card-btn"
        type="button"
        ?disabled=${isRemoveProductFromWishListLoading()}
        @click=${() => this.removeProductFromWishListItem()}
      >
        ${this.uxtIconReady ? html` <uxt-icon icon="heart-fill" color="primary" size="lg"></uxt-icon>` : nothing}
      </button>
    `;
  }

  /**
   * Retrieves the remove wishlist toast message.
   */
  private getWishListToast() {
    return html` <div class="remove-wish-list-toast"></div>`;
  }

  /**
   * Gets the product ISBN10
   */
  private getProductIsbn10(): string {
    return this.productCardData!.isbn || '';
  }

  /**
   * Removes the product from wish list item.
   */
  private removeProductFromWishListItem() {
    const product = this.productCardData!;
    const wishList = this.wishList!;
    const wishListItemId = getProductFromWishList(product, wishList)?.wishlist_item_id!;
    const request = getRemoveProductFromWishListRequest(wishList, wishListItemId);

    this.wishListAnalytics.removeProductFromWishList(
      this,
      wishList,
      this.user!,
      product,
    );

    store
      .dispatch(removeProductFromWishList(request))
      .then((): void => this.handleApiResponse(WishListProductAction.REMOVE, product, wishList))
      .catch((): void => this.handleErrorResponse());
  }

  /**
   * Handles the API response when removing a product from wish list item.
   *
   * @param action
   * @param product
   * @param wishList
   */
  private handleApiResponse(action: WishListProductAction, product?: ProductCardData, wishList?: WishList): void {
    if (isRemoveProductFromWishListSuccess()) {
      return this.handleSuccessResponse(action, product, wishList);
    }

    this.handleErrorResponse();
  }

  /**
   * Handles success response when removing a product from wish list item.
   *
   * @param action
   * @param product
   * @param wishList
   */
  private handleSuccessResponse(action: WishListProductAction, product?: ProductCardData, wishList?: WishList): void {
    render(renderUpdateProductSuccessToast(action, product, wishList), this.getToast());
    store.dispatch(fetchWishListByWishListId(this.wishList!.wishlist_id));
    store.dispatch(setCurrentWishList(wishList));
    this.offCanvasView &&
      waitForUser().then((): void => {
        store.dispatch(fetchAllWishLists());
      });
  }

  /**
   * Handles error response when removing a product from wish list item.
   */
  private handleErrorResponse(): void {
    render(
      renderErrorToast({
        error: ErrorToastWishList.REMOVING_PRODUCT_FROM_WISH_LIST,
        displayLoadingTemplate: true,
        autoHide: false,
        hideClose: true,
      }),
      this.getToast()
    );
  }

  /**
   * Gets the toast element.
   */
  private getToast(): HTMLElement {
    return this.shadowRoot?.querySelector('.remove-wish-list-toast') as HTMLElement;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-item-card-display': WishListItemCardDisplay;
  }
}
